import type { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AnyPresentableResource } from '@timelyapp/integration-cloud-api-spec';
import { fetchClient } from '@timelyapp/integration-cloud-sdk/api';
import { getAnalyticsSessionIdAsNumber } from 'tracking/session';
import api from '~api';
import { getResolvedTimesheetAssistant } from '~domains/ai/predictions/redux/predictions.selectors';
import { errorMessageFactory } from '~factories/api';
import { getIsDayLockedByDate } from '~redux/modules/dayProperties.selectors';
import { fetchHours } from '~redux/modules/hours.actions';
import { getContextualTicAccounts } from '~redux/modules/tic/tic.auth.selectors';
import { timelyApi } from '~redux/modules/timely/api-slice/timely.apiSlice';
import { showToast } from '~redux/modules/toasts.actions';
import { getCurrentAccountId } from '~redux/selectors/accounts';
import { getUserId } from '~redux/selectors/user';
import type { IState } from '~redux/state';
import timelyAnalytics from '~tracking/timely-analytics';
import type { ExternalHourLink } from '~types/hour';
import type { Project } from '~types/project';
import { fetchInBatchesOf } from '~utils/api';
import debug from '~utils/debug';
import type { ProposalEntry } from '../types/types.TimesheetProposals';
import { getAnalyticsForTimesheetSubmitted } from '../utils/util.analytics';
import type { ClaimSpec } from '../utils/util.entryClaim';
import { executeClaim } from '../utils/util.entryClaim.execute';
import { entryIdsWithItems } from '../utils/util.itemContainment';
import type { TimeRange } from '../utils/util.timesheetTransformers';
import { getIsPatchingTimesheetProposalEntry } from './tic.timesheetProposals.selectors';
import {
  DEFAULT_QUERY_PARAMS,
  setAcceptTimesheetLoading,
  setFlashingEntryIds,
  setFullProjects,
  setIsLoadingSelectedProjects,
  setIsRegeneratingEntry,
  setRegeneratingNotesForDate,
  setRevertTimesheetLoading,
} from './tic.timesheetProposals.slice';

/**
 * Convert a resource to an ExternalHourLink
 */
function resourceToExternalLink(resource: AnyPresentableResource): ExternalHourLink | null {
  if (!resource.tool?.id) {
    return null;
  }

  return {
    provider_type: resource.tool.id,
    provider_id: resource.metadata?.external_code ?? null,
    external_id: resource.external_id,
    uri: resource.uri,
  };
}

export const acceptTimesheet = createAsyncThunk<
  void,
  {
    /**
     * YYYY-MM-DD we want to accept predictions for.
     */
    date?: string;
    proposalId: string; // Required proposal ID for RTK Query integration
  },
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/ai/acceptTimesheet', async (params, thunkAPI) => {
  const { proposalId, date } = params;
  const state = thunkAPI.getState();

  thunkAPI.dispatch(setAcceptTimesheetLoading(true));

  try {
    if (getIsPatchingTimesheetProposalEntry(state)) {
      throw new Error('Timesheet entry changes are still saving');
    }

    const userId = getUserId(state);
    const timelyAccountId = getCurrentAccountId(state);

    if (!userId || !timelyAccountId) {
      throw new Error('Missing required user or account information');
    }

    debug.template.ai('Starting timesheet acceptance', {
      proposalId,
      date,
      userId,
      timelyAccountId,
      subtopic: 'Accept timesheet proposal',
    });

    // Fetch the proposal so we can resolve each entry's resource_id into the
    // per-entry external_links the submit payload expects — the FE owns
    // resource→external_link resolution. The submit does the hour write +
    // status flip server-side, so this is the only pre-step left.
    const proposalResult = await thunkAPI.dispatch(
      timelyApi.endpoints.getTimesheetProposal.initiate(proposalId, { forceRefetch: true })
    );

    if (!proposalResult.data?.proposal) {
      throw new Error('Could not fetch timesheet proposal data');
    }

    const proposal = proposalResult.data.proposal;
    const proposalDate = date || proposal.date;

    // FE pre-flight only — the server also rejects a submit on a locked day,
    // so this is a fast-fail, not the authority.
    const isDayLocked = getIsDayLockedByDate(thunkAPI.getState(), proposalDate);
    if (isDayLocked) {
      throw new Error('The day is locked. Unlock the day to submit your timesheet.');
    }

    // Submit only writes hours for project-assigned entries; orphan items
    // (proposal.unassigned_items) deliberately stay in the proposal's
    // Unassigned bucket. If there's nothing to submit — e.g., a proposal
    // where every activity is still unassigned — surface that explicitly
    // instead of throwing the misleading "No entries found in timesheet
    // proposal" error.
    if (!proposal.entries || proposal.entries.length === 0) {
      throw new Error(
        'Nothing to submit — assign at least one activity to a project before submitting.'
      );
    }

    const { workspace } = getContextualTicAccounts(state);

    // Resolve resources for entries that have a resource_id, building the
    // per-entry external_links the submit body carries.
    const resourceIds = proposal.entries
      .map(entry => entry.resource_id)
      .filter((id): id is string => !!id);

    const resourceMap = new Map<string, ExternalHourLink>();
    if (resourceIds.length > 0 && workspace?.id) {
      // Fetch resources in parallel using the ts-rest client
      const resourcePromises = resourceIds.map(async resourceId => {
        try {
          const result = await fetchClient.resources.getResource({
            params: { resourceId },
          });

          if (result.status === 200 && result.body) {
            const link = resourceToExternalLink(result.body);
            if (link) {
              resourceMap.set(resourceId, link);
            }
          }
        } catch (error) {
          // Silently fail - if resource can't be fetched, just skip external link
          debug.template.ai('Failed to fetch resource for timesheet entry', {
            resourceId,
            error: error instanceof Error ? error.message : String(error),
            subtopic: 'Resource fetch failure',
          });
        }
      });

      await Promise.allSettled(resourcePromises);
    }

    // Build the submit payload: one entry per persisted proposal entry, keyed
    // by its Rails id (bigint → number), carrying any resolved external_links.
    // Entries without a resource_id (or whose resource failed to resolve) send
    // an empty link set.
    const entries = proposal.entries.map(entry => ({
      id: entry.id,
      external_links: entry.resource_id
        ? [resourceMap.get(entry.resource_id)].filter((link): link is ExternalHourLink => !!link)
        : [],
    }));

    // Rails submit: writes the hours, flips the proposal to accepted, and
    // returns the created hour ids — one round-trip replacing the legacy
    // pre-delete + bulkSaveHours + status-PATCH choreography.
    const submitResult = await thunkAPI.dispatch(
      timelyApi.endpoints.submitTimesheetProposal.initiate({
        id: proposalId,
        body: { entries },
      })
    );

    if ('error' in submitResult && submitResult.error) {
      // The submit is atomic: it either logs EVERY entry (200) or refuses the
      // whole submit (422) — there is no partial 200. On a 422 the body is
      // `{ message, row_errors }`, where each row_error is `{ entry_id, messages }`
      // carrying the real HourForm reason (e.g. "Label has been archived"). When
      // `row_errors` is present, surface those per-row reasons — flattened +
      // deduped — instead of the unhelpful generic envelope `message`.
      //
      // The body lands at `error.response.row_errors` because timelyApi is a
      // custom baseQuery (NOT fetchBaseQuery), so the parsed 422 body is on
      // `.response` (see api/core/base.ts `handleErrors`). `error.response` is
      // untyped (the codegen types only the 2xx success shape), so read it
      // defensively.
      const rowErrors = (
        submitResult.error as { response?: { row_errors?: { messages?: string[] }[] } }
      ).response?.row_errors;
      if (rowErrors && rowErrors.length > 0) {
        const reasons = Array.from(new Set(rowErrors.flatMap(rowError => rowError.messages ?? [])));
        if (reasons.length > 0) {
          throw new Error(reasons.join('; '));
        }
      }
      // No usable `row_errors` → fall back to the server-authoritative envelope
      // `message` (e.g. "The day is locked for this user"). errorMessageFactory
      // reads `error.response.message` and falls back to a readable string for
      // non-Rails error shapes.
      throw new Error(errorMessageFactory(submitResult.error));
    }

    const createdHourIds = submitResult.data?.created_hour_ids ?? [];

    // Refetch hours to update the calendar view and TIC timeline (hours are shown separately from memories)
    await thunkAPI.dispatch(fetchHours({ since: proposalDate, upto: proposalDate, userId }));

    debug.template.ai('Timesheet acceptance completed successfully', {
      proposalId,
      savedHoursCount: createdHourIds.length,
      subtopic: 'Acceptance complete',
      color: 'green',
    });

    const savedWord = createdHourIds.length === 1 ? 'entry' : 'entries';
    thunkAPI.dispatch(
      showToast({
        title: 'Timesheet submitted successfully',
        message: `${createdHourIds.length} time ${savedWord} saved to your timesheet.`,
        type: 'success',
        dismissAfter: 5000,
      })
    );

    thunkAPI.dispatch(setAcceptTimesheetLoading(false));

    // Analytics: fetch accepted count then track (fire-and-forget, never blocks UX)
    thunkAPI
      .dispatch(
        timelyApi.endpoints.listTimesheetProposals.initiate(
          { status: 'accepted', limit: 1 },
          { subscribe: false }
        )
      )
      .then(result => {
        // `meta` is an untyped object on the Rails list response; read
        // totalCount defensively (requires Rails list to populate meta.totalCount).
        const submissionNumber = (result.data?.meta as { totalCount?: number } | undefined)
          ?.totalCount;
        timelyAnalytics.trackTic('TA2 Submitted AI Generated Timesheet', {
          ...getAnalyticsForTimesheetSubmitted(proposal),
          timesheet_id: proposalId,
          submission_number: submissionNumber,
        });
      });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

    debug.template.ai('Timesheet acceptance failed', {
      proposalId,
      error: errorMessage,
      subtopic: 'Acceptance error',
    });

    thunkAPI.dispatch(
      showToast({
        title: 'Failed to submit timesheet',
        message: errorMessage,
        type: 'danger',
        dismissAfter: 8000,
      })
    );

    thunkAPI.dispatch(setAcceptTimesheetLoading(false));

    throw error;
  }
});

export const revertTimesheet = createAsyncThunk<
  void,
  {
    /**
     * YYYY-MM-DD date of the timesheet to revert.
     */
    date?: string;
    proposalId: string; // Required proposal ID for RTK Query integration
  },
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/ai/revertTimesheet', async (params, thunkAPI) => {
  const { proposalId, date } = params;
  const state = thunkAPI.getState();

  thunkAPI.dispatch(setRevertTimesheetLoading(true));

  try {
    const userId = getUserId(state);
    const timelyAccountId = getCurrentAccountId(state);

    if (!userId || !timelyAccountId) {
      throw new Error('Missing required user or account information');
    }

    debug.template.ai('Starting timesheet revert', {
      proposalId,
      date,
      userId,
      timelyAccountId,
      subtopic: 'Revert timesheet proposal',
    });

    // Rails unsubmit deletes the proposal's hours and flips it back to draft
    // server-side, returning the partial-revert accounting. No body, bare-string
    // proposalId (flattenArg).
    const revertResult = await thunkAPI.dispatch(
      timelyApi.endpoints.unsubmitTimesheetProposal.initiate(proposalId)
    );

    if ('error' in revertResult && revertResult.error) {
      // Surface the server-authoritative reason (422 `{ message }` →
      // `error.response.message` for timelyApi's custom baseQuery) instead of a
      // hardcoded generic, so e.g. "The day is locked for this user" reaches the
      // toast. errorMessageFactory falls back to a readable string for non-Rails
      // error shapes.
      throw new Error(errorMessageFactory(revertResult.error));
    }

    if (!revertResult.data?.proposal) {
      throw new Error('Could not revert timesheet proposal');
    }

    const { proposal, partial_revert, deleted_count, expected_count } = revertResult.data;

    await thunkAPI.dispatch(fetchHours({ since: proposal.date, upto: proposal.date, userId }));

    debug.template.ai('Reverted timesheet proposal', {
      proposalId,
      deletedCount: deleted_count,
      expectedCount: expected_count,
      partialRevert: partial_revert,
      subtopic: 'Revert complete',
    });

    const entriesWord = deleted_count === 1 ? 'entry' : 'entries';
    thunkAPI.dispatch(
      showToast(
        partial_revert
          ? {
              title: 'Timesheet reverted with fewer entries than expected',
              message:
                `${deleted_count} of ${expected_count} time ${entriesWord} removed and ` +
                'returned to draft state. The remaining entries were already missing.',
              type: 'warning',
              dismissAfter: 8000,
            }
          : {
              title: 'Timesheet reverted successfully',
              message: `${deleted_count} time ${entriesWord} removed from your timesheet and returned to draft state.`,
              type: 'success',
              dismissAfter: 5000,
            }
      )
    );

    thunkAPI.dispatch(setRevertTimesheetLoading(false));
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

    debug.template.ai('Timesheet revert failed', {
      proposalId,
      error: errorMessage,
      subtopic: 'Revert error',
    });

    thunkAPI.dispatch(
      showToast({
        title: 'Failed to revert timesheet',
        message: errorMessage,
        type: 'danger',
        dismissAfter: 8000,
      })
    );

    thunkAPI.dispatch(setRevertTimesheetLoading(false));

    throw error;
  }
});

export const deleteTimesheet = createAsyncThunk<
  void,
  {
    proposalId: string;
  },
  { dispatch: ThunkDispatch<IState, any, Action> }
>('tic/ai/deleteTimesheet', async (params, thunkAPI) => {
  const { proposalId } = params;

  // pre-fetch required: DELETE returns only {message}; toast needs entry count
  const proposalResult = await thunkAPI.dispatch(
    timelyApi.endpoints.getTimesheetProposal.initiate(proposalId)
  );

  if (!proposalResult.data?.proposal) {
    throw new Error('Could not fetch timesheet proposal data');
  }

  debug.template.ai('Starting timesheet deletion', {
    proposalId,
    subtopic: 'Delete timesheet proposal',
  });

  // unwrap() so a failed delete rejects — the raw mutation dispatch RESOLVES
  // even on error (the error lives in the result object), which would show the
  // success toast for a delete that never happened.
  thunkAPI
    .dispatch(timelyApi.endpoints.deleteTimesheetProposal.initiate(proposalId))
    .unwrap()
    .then(() => {
      const successMessage = `Timesheet proposal ${proposalId} with ${proposalResult.data?.proposal?.entries?.length} entries deleted successfully`;
      debug.template.ai('Timesheet deleted successfully', {
        proposalId,
        subtopic: 'Delete success',
      });

      thunkAPI.dispatch(
        showToast({
          title: 'Timesheet deleted successfully',
          message: successMessage,
          type: 'success',
        })
      );
    })
    .catch(error => {
      const errorMessage = errorMessageFactory(error);
      debug.template.ai('Timesheet deletion failed', {
        proposalId,
        error: errorMessage,
        subtopic: 'Delete error',
      });

      thunkAPI.dispatch(
        showToast({
          title: 'Failed to delete timesheet',
          message: errorMessage,
          type: 'danger',
          dismissAfter: 8000,
        })
      );
    });
});

const inFlightProjectIds = new Set<number>();

export const fetchFullProjects = createAsyncThunk<
  void,
  number[],
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/ai/fetchFullProjects', async (projectIds, thunkAPI) => {
  const idsToFetch = projectIds.filter(id => !inFlightProjectIds.has(id));
  if (idsToFetch.length === 0) return;

  idsToFetch.forEach(id => inFlightProjectIds.add(id));

  thunkAPI.dispatch(setIsLoadingSelectedProjects(true));

  try {
    debug.template.ai('Fetching missing project data for timesheet proposal', {
      projectIds: idsToFetch,
      count: idsToFetch.length,
      subtopic: 'Fetch missing projects',
    });

    const projects: Project[] = await fetchInBatchesOf(50, idsToFetch, batch =>
      api.getProjectIds(batch, { includeTotals: false })
    );

    if (projects.length > 0) {
      thunkAPI.dispatch(setFullProjects(projects));
    }

    debug.template.ai('Missing projects fetched successfully', {
      fetchedCount: projects.length,
      requestedCount: idsToFetch.length,
      subtopic: 'Fetch success',
      color: 'green',
    });

    thunkAPI.dispatch(setIsLoadingSelectedProjects(false));
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

    debug.template.ai('Failed to fetch missing projects', {
      projectIds: idsToFetch,
      error: errorMessage,
      subtopic: 'Fetch error',
    });

    thunkAPI.dispatch(setIsLoadingSelectedProjects(false));

    throw error;
  } finally {
    idsToFetch.forEach(id => inFlightProjectIds.delete(id));
  }
});

export const regenerateEntryNotes = createAsyncThunk<
  void,
  {
    date: string;
    proposalId: string;
    entryId?: string; // Optional: regenerate only this specific entry
  },
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/ai/regenerateEntryNotes', async (params, thunkAPI) => {
  const { date, proposalId, entryId } = params;
  const state = thunkAPI.getState();

  try {
    const timelyAccountId = getCurrentAccountId(state);
    const timelyUserId = getUserId(state);

    if (!timelyAccountId || !timelyUserId) {
      throw new Error('Missing required account or user information');
    }

    // Resolve the note language from the same source the generate path uses
    // (admin-enforced → user workspace → system default) so regenerated notes
    // honour the workspace's language preference.
    const note_language =
      getResolvedTimesheetAssistant(state).note_language ?? DEFAULT_QUERY_PARAMS.note_language;

    if (entryId) {
      thunkAPI.dispatch(setIsRegeneratingEntry({ entryId, isRegenerating: true }));
    } else {
      thunkAPI.dispatch(setRegeneratingNotesForDate({ date, isRegenerating: true }));
    }

    debug.template.ai('Starting note regeneration', {
      proposalId,
      date,
      entryId,
      subtopic: entryId ? 'Regenerate single entry note' : 'Regenerate entry notes',
    });

    timelyAnalytics.trackTic('TA2 Requested Note Regeneration', {
      timesheet_id: proposalId,
      timesheet_date: date,
      entry_id: entryId,
      regeneration_type: entryId ? 'single' : 'bulk',
    });

    // The regen TRIGGER itself can fail (a 400 on a RegenerationError when the
    // engine is unreachable / returns non-2xx). When it does, NO Pusher will
    // ever come (the engine was never reached), so the catch below is the ONLY
    // thing that can clear the per-entry shimmer + tell the user. Surface the
    // trigger error instead of swallowing it — check `'error' in result` like
    // acceptTimesheet/revertTimesheet. timelyApi is a custom baseQuery (the
    // parsed body is on `error.response`); errorMessageFactory reads
    // `error.response.message`.
    const triggerResult = await thunkAPI.dispatch(
      timelyApi.endpoints.regenerateEntryNotes.initiate({
        date,
        timely_account_id: timelyAccountId,
        timely_user_id: timelyUserId,
        entry_id: entryId,
        session_id: getAnalyticsSessionIdAsNumber(),
        metadata: { note_language },
      })
    );

    if ('error' in triggerResult && triggerResult.error) {
      throw new Error(errorMessageFactory(triggerResult.error));
    }

    debug.template.ai('Note regeneration triggered successfully', {
      proposalId,
      date,
      entryId,
      subtopic: 'Regeneration started',
      color: 'green',
    });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

    debug.template.ai('Note regeneration failed', {
      proposalId,
      date,
      entryId,
      error: errorMessage,
      subtopic: 'Regeneration error',
    });

    if (entryId) {
      thunkAPI.dispatch(setIsRegeneratingEntry({ entryId, isRegenerating: false }));
    } else {
      thunkAPI.dispatch(setRegeneratingNotesForDate({ date, isRegenerating: false }));
    }

    thunkAPI.dispatch(
      showToast({
        title: 'Failed to regenerate note',
        message: errorMessage,
        type: 'danger',
        dismissAfter: 8000,
      })
    );

    throw error;
  }
});

/**
 * Claim one or more time ranges for a project. Used by:
 * - the timeline drag-to-claim gesture (single range, non-null project), and
 * - the reassign-selection UI in ReassignmentControls (one range per contiguous
 *   group of selected items, project optional — null = release claim).
 * Goes through the claim primitive so adjacent same-identity ranges merge
 * transparently.
 */
export const claimRangesForProject = createAsyncThunk<
  { targetEntryIds: string[]; affectedEntryIds: string[] },
  {
    proposalId: string;
    ranges: TimeRange[];
    projectId: number | null;
    labelIds: number[];
    resourceId: string | null;
  },
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('tic/ai/claimRangesForProject', async (params, thunkAPI) => {
  const { proposalId, ranges, projectId, labelIds, resourceId } = params;

  if (ranges.length === 0) {
    return { targetEntryIds: [], affectedEntryIds: [] };
  }

  const state = thunkAPI.getState();
  // Read the proposal cache the display renders. `flattenArg` keys it by the
  // BARE string id (not `{ id }`).
  const cachedQuery = timelyApi.endpoints.getTimesheetProposal.select(proposalId)(state);
  let proposal = cachedQuery.data?.proposal;
  if (!proposal) {
    const proposalResult = await thunkAPI.dispatch(
      timelyApi.endpoints.getTimesheetProposal.initiate(proposalId)
    );
    proposal = proposalResult.data?.proposal;
  }

  if (!proposal) {
    thunkAPI.dispatch(
      showToast({
        message: 'Unable to apply changes — no timesheet proposal available',
        type: 'danger',
      })
    );
    throw new Error('Could not fetch timesheet proposal data');
  }

  // Date for newly-created entries is the proposal's own day, not the caller's
  // local calendar.
  const date = proposal.date;

  try {
    const baseClaim: Omit<ClaimSpec, 'range'> = {
      projectId,
      labelIds: [...labelIds].sort((a, b) => a - b),
      resourceId,
      date,
      note: null,
    };
    const allTargetEntryIds = new Set<string>();
    const allAffectedEntryIds = new Set<string>();
    // Entries the claim created from scratch. These carry the claimed time but
    // no note — the source entry they were carved out of is deleted/split away.
    // We regenerate a note for them below so reassigning doesn't lose context.
    const createdTargetEntryIds = new Set<string>();

    // The Rails (timely) cache stores entry ids as NUMBERS; the claim machinery
    // (computeClaimEffects / executeClaim / the timelyApi optimistic patches) is
    // string-keyed end-to-end (ProposalEntry.id is string). Coerce at this read
    // boundary — the same `String(id)` normalization the display transformer does
    // — so the effects' entryId matches the string-comparison in the optimistic
    // cache patches. Without this the number/string mismatch would silently no-op
    // every optimistic delete/patch.
    const toStringIdEntries = (rows: NonNullable<typeof proposal.entries>): ProposalEntry[] =>
      rows.map(entry => ({ ...entry, id: String(entry.id) }));

    let entries: ProposalEntry[] = toStringIdEntries(proposal.entries ?? []);

    for (const range of ranges) {
      const result = await executeClaim({
        claim: { ...baseClaim, range },
        entries,
        proposalId: proposal.id,
        dispatch: thunkAPI.dispatch,
      });

      if (result.targetEntryId) {
        allTargetEntryIds.add(result.targetEntryId);
        if (result.targetEntryCreated) createdTargetEntryIds.add(result.targetEntryId);
      }
      result.affectedEntryIds.forEach(id => allAffectedEntryIds.add(id));

      // See executeClaim's docstring: callers running multi-range must refresh
      // entries between iterations. Different-identity entries may span
      // gap-separated ranges (e.g. one big unassigned block touched by two
      // selections), so each iteration needs the post-effect geometry from the
      // optimistic cache.
      const refreshedQuery = timelyApi.endpoints.getTimesheetProposal.select(proposalId)(
        thunkAPI.getState()
      );
      const refreshedEntries = refreshedQuery.data?.proposal?.entries;
      entries = refreshedEntries ? toStringIdEntries(refreshedEntries) : entries;
    }

    // Synthetic "unassigned" key for flash UX when target was a release.
    if (projectId === null) allTargetEntryIds.add('unassigned');

    thunkAPI.dispatch(
      setFlashingEntryIds(Array.from(new Set([...allTargetEntryIds, ...allAffectedEntryIds])))
    );
    // Invalidate the proposal-list cache (so list views re-fetch summaries),
    // but NOT the full proposal cache — each effect's optimistic update has
    // already brought it to the post-claim state, and refetching there causes
    // back-to-back re-renders that interrupt the flash animation.
    thunkAPI.dispatch(timelyApi.util.invalidateTags(['TimesheetProposals']));

    // Assigning unassigned time to a project replaces the source entry with a
    // fresh one, which has no note. Regenerate notes for just those new entries
    // so users keep the context they had. Release claims (projectId === null)
    // produce no created entries, so this no-ops for "move to unassigned".
    // Fire-and-forget: a regen failure must not fail the claim that already
    // succeeded — regenerateEntryNotes surfaces its own error toast.
    //
    // Regenerate PER created entry, not date-wide: only the freshly-created
    // entries lost their note (carved out of a deleted/split source). A
    // date-wide regenerate would also rewrite OTHER entries the user manually
    // edited that same day — clobbering hand-curated notes. The per-entry id
    // round-trips: executeClaim creates the entry and captures its real numeric
    // id, and the server's regenerate keys `where(id: entry_id)` on it. The
    // entryIdsWithItems gate drops created entries with no contained activities
    // (drag-claiming empty space), which would otherwise trip the server's
    // no-activities guard.
    for (const entryId of entryIdsWithItems(createdTargetEntryIds, proposal.items ?? [], entries)) {
      void thunkAPI.dispatch(regenerateEntryNotes({ date, proposalId, entryId }));
    }

    return {
      targetEntryIds: Array.from(allTargetEntryIds),
      affectedEntryIds: Array.from(allAffectedEntryIds),
    };
  } catch (error) {
    console.error('Failed to apply claim:', error);
    thunkAPI.dispatch(timelyApi.util.invalidateTags(['TimesheetProposal', 'TimesheetProposals']));
    thunkAPI.dispatch(showToast({ message: 'Failed to apply changes', type: 'danger' }));
    throw error;
  }
});
